import { TELEMETRY } from '@tfa/shared'
import { API_BASE_URL } from '../services/api'

/**
 * Продуктовая телеметрия мини-аппа: какие экраны открывают, сколько на них
 * проводят времени и по чему нажимают.
 *
 * Устройство намеренно простое и «дешёвое»:
 *  • события копятся в памяти и уходят пачкой раз в FLUSH_INTERVAL_SEC;
 *  • время тикает ТОЛЬКО когда вкладка видима — свёрнутый Telegram не должен
 *    превращаться в часы «использования»;
 *  • на закрытие приложения буфер уходит запросом с keepalive: он переживает
 *    выгрузку страницы, которую Telegram WebView делает без предупреждения.
 *
 * Телеметрия не имеет права ломать приложение: любая ошибка отправки молча
 * гасится, а буфер отбрасывается (копить его вечно хуже, чем потерять).
 */

type EventType = 'view' | 'time' | 'click'

interface TelemetryEvent {
  type: EventType
  screen: string
  target?: string
  value?: number
}

const SESSION_KEY_STORAGE = 'tfa_telemetry_session'
const SESSION_TOUCHED_STORAGE = 'tfa_telemetry_session_at'

let buffer: TelemetryEvent[] = []
let currentScreen = ''
let secondsOnScreen = 0
let flushTimer: ReturnType<typeof setInterval> | null = null
let tickTimer: ReturnType<typeof setInterval> | null = null
let getToken: (() => string | null) | null = null
let enabled = false

function newSessionKey(): string {
  const rnd = globalThis.crypto?.randomUUID?.()
  return rnd ?? `s-${Math.random().toString(36).slice(2)}-${Date.now()}`
}

/**
 * Ключ визита. Возврат после паузы длиннее SESSION_IDLE_TIMEOUT_MIN считается
 * новым визитом — иначе «средняя длительность сессии» превращается в среднюю
 * длительность недели.
 */
function sessionKey(): string {
  const now = Date.now()
  let key: string | null = null
  let touchedAt = 0
  try {
    key = sessionStorage.getItem(SESSION_KEY_STORAGE)
    touchedAt = Number(sessionStorage.getItem(SESSION_TOUCHED_STORAGE) ?? 0)
  } catch {
    // Приватный режим WebView может запретить storage — тогда каждый заход
    // будет считаться отдельным визитом. Это хуже, но не ломает ничего.
  }
  const expired = !key || now - touchedAt > TELEMETRY.SESSION_IDLE_TIMEOUT_MIN * 60_000
  if (expired) key = newSessionKey()
  try {
    sessionStorage.setItem(SESSION_KEY_STORAGE, key!)
    sessionStorage.setItem(SESSION_TOUCHED_STORAGE, String(now))
  } catch {
    /* см. выше */
  }
  return key!
}

function push(event: TelemetryEvent) {
  if (!enabled) return
  // Потолок буфера — страховка на случай, если отправка долго не проходит:
  // расти бесконечно он не должен.
  if (buffer.length >= TELEMETRY.MAX_EVENTS_PER_FLUSH) return
  buffer.push(event)
}

/** Слить накопленное время текущего экрана в буфер. */
function drainScreenTime() {
  if (secondsOnScreen > 0 && currentScreen) {
    push({ type: 'time', screen: currentScreen, value: Math.min(secondsOnScreen, TELEMETRY.MAX_SECONDS_PER_FLUSH) })
    secondsOnScreen = 0
  }
}

function payload() {
  return JSON.stringify({
    sessionId: sessionKey(),
    tzOffsetMinutes: new Date().getTimezoneOffset(),
    appBuildId: (import.meta.env.VITE_BUILD_ID as string | undefined) ?? undefined,
    events: buffer,
  })
}

function flush(closing = false) {
  if (!enabled) return
  drainScreenTime()
  if (buffer.length === 0) return

  const token = getToken?.()
  if (!token) {
    // Пока не авторизованы, копить бессмысленно — API всё равно отвергнет.
    buffer = []
    return
  }

  const body = payload()
  buffer = []
  const url = `${API_BASE_URL.replace(/\/$/, '')}/telemetry/beat`

  // Хвост при закрытии приложения доставляем через keepalive, а НЕ sendBeacon:
  // beacon не умеет заголовки, и токен пришлось бы класть в query — прямиком в
  // логи запросов. keepalive живёт после выгрузки страницы и сохраняет Bearer.
  void fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body,
    keepalive: closing,
  }).catch(() => {
    // Телеметрия — не то, ради чего стоит показывать ошибку пользователю.
  })
}

/** Экран открыт. screen — шаблон маршрута, а не сырой путь с идентификаторами. */
export function trackScreen(screen: string) {
  if (!enabled || screen === currentScreen) return
  drainScreenTime()
  currentScreen = screen
  push({ type: 'view', screen })
}

/** Клик по значимому элементу. */
export function trackClick(target: string, screen = currentScreen) {
  push({ type: 'click', screen: screen || 'unknown', target })
}

/**
 * Включить телеметрию. tokenGetter отдаёт актуальный JWT (он меняется при
 * перелогине), поэтому храним функцию, а не значение.
 */
export function startTelemetry(tokenGetter: () => string | null) {
  if (enabled) return
  enabled = true
  getToken = tokenGetter

  tickTimer = setInterval(() => {
    // Время идёт только при видимой вкладке: у мини-аппа «свернули Telegram»
    // — самый частый способ закончить сеанс, и он не должен выглядеть как
    // многочасовое использование.
    if (document.visibilityState === 'visible') secondsOnScreen += TELEMETRY.TICK_SEC
  }, TELEMETRY.TICK_SEC * 1000)

  flushTimer = setInterval(() => flush(false), TELEMETRY.FLUSH_INTERVAL_SEC * 1000)

  document.addEventListener('visibilitychange', onVisibilityChange)
  window.addEventListener('pagehide', onPageHide)
}

function onVisibilityChange() {
  // Уход в фон — момент, когда приложение с большой вероятностью больше не
  // вернётся: отдаём накопленное сразу.
  if (document.visibilityState === 'hidden') flush(true)
}

function onPageHide() {
  flush(true)
}

export function stopTelemetry() {
  if (!enabled) return
  flush(true)
  enabled = false
  if (tickTimer) clearInterval(tickTimer)
  if (flushTimer) clearInterval(flushTimer)
  tickTimer = null
  flushTimer = null
  document.removeEventListener('visibilitychange', onVisibilityChange)
  window.removeEventListener('pagehide', onPageHide)
}

/** Только для тестов: текущее состояние буфера. */
export function __telemetryStateForTests() {
  return { buffer: [...buffer], currentScreen, secondsOnScreen, enabled }
}

/** Только для тестов: сброс модуля между кейсами. */
export function __resetTelemetryForTests() {
  buffer = []
  currentScreen = ''
  secondsOnScreen = 0
  enabled = false
  getToken = null
  if (tickTimer) clearInterval(tickTimer)
  if (flushTimer) clearInterval(flushTimer)
  tickTimer = null
  flushTimer = null
}
